import { ActionContext, Module } from 'vuex'
import { LaunchpadState, RootState } from '../store/state_models'

import { IStepInfo } from '../models/launchpad_progress'
import { LaunchpadProgress } from '../models'
import { LaunchpadService } from '../services'
import moment from 'moment-timezone'
import { shouldHideLaunchpad } from '../pmd/composition/userPermission'

function is97Plan(stripeActivePlan: string) {
  return stripeActivePlan === 'agency_monthly_97' || stripeActivePlan === 'agency_annual_97'
}

export const launchpad: Module<LaunchpadState, RootState> = {
  namespaced: true,
  state: {
    isAgencyLaunchpadAssigned: null,
    type: '', // 'agency' or 'location'
    stepInfo: {},
    isPiPVisible: false,
    videoCompletionRate: 0,
    selectedProductId: '',
    selectedProducts: [],
    selectedActionCompletionPercentage: 0,
    segmentId: '',
    nudgeCategory: {},
    nudgeRules: [],
    ruleNameTriggered: '',
    currentNudgeProduct: null,
  },
  getters: {
    isAgencyLaunchpadAssigned: (state: LaunchpadState) => {
      return state.isAgencyLaunchpadAssigned
    },
  },
  mutations: {
    setIsAgencyLaunchpadAssigned: (state: LaunchpadState, payload: { isAgencyLaunchpadAssigned: boolean }) => {
      state.isAgencyLaunchpadAssigned = payload.isAgencyLaunchpadAssigned
    },
    setPiPVisibility: (state: LaunchpadState, payload: { visibility: boolean }) => {
      state.isPiPVisible = payload.visibility
    },
    setVideoCompletionRate: (state: LaunchpadState, payload: { videoCompletionRate: number }) => {
      state.videoCompletionRate = payload.videoCompletionRate
    },
    setSelectedProductId: (state: LaunchpadState, payload: { selectedProductId: string }) => {
      state.selectedProductId = payload.selectedProductId
    },
    setSelectedActionCompletionPercentage: (
      state: LaunchpadState,
      payload: { selectedActionCompletionPercentage: number }
    ) => {
      state.selectedActionCompletionPercentage = payload.selectedActionCompletionPercentage
    },
    setSegmentId: (state: LaunchpadState, payload: { segmentId: string }) => {
      state.segmentId = payload.segmentId
    },
    setSelectedProducts: (state: LaunchpadState, payload: { selectedProducts: string[] }) => {
      state.selectedProducts = payload.selectedProducts
    },
    setNudgeCategoryDetails: (state: LaunchpadState, payload: { nudgeCategory: object }) => {
      state.nudgeCategory = payload.nudgeCategory
    },
    setNudgeRuleDetails: (state: LaunchpadState, payload: { nudgeRules: object[] }) => {
      state.nudgeRules = payload.nudgeRules
    },
    setNudgeRuleTriggered: (state: LaunchpadState, payload: { ruleNameTriggered: string }) => {
      state.ruleNameTriggered = payload.ruleNameTriggered
    },
    setCurrentNudgeProduct: (state: LaunchpadState, payload: { currentNudgeProduct: object }) => {
      state.currentNudgeProduct = payload.currentNudgeProduct
    },
  },
  actions: {
    redirectToLaunchpad: {
      handler: (context: ActionContext<LaunchpadState, RootState>, locationId: string): Promise<any> => {
        return new Promise(async resolve => {
          try {
            if (!locationId) {
              resolve(false)
            }

            // Remove if firebase changes are done by Pragnesh and Mayur
            const company = context.rootState?.company?.company
            const userScopes = context.rootState?.user?.user?.scopes
            let onboardingInfo = context.rootState?.onboarding?.onboardingInfo

            if (
              !userScopes?.includes('launchpad/location.write') ||
              shouldHideLaunchpad(
                company?.hide_launchpad,
                context.rootGetters?.['featureFlags/getForLocation']?.('location-launchpad-v2')
              )
            ) {
              return resolve(false)
            }

            if (onboardingInfo?.enableLaunchpadV2 === undefined) {
              onboardingInfo = await context.dispatch(
                'onboarding/getOnboardingInfo',
                { companyId: company?.id, forceRefresh: true },
                { root: true }
              )
            }

            if (company && 'date_added' in company && 'stripe_active_plan' in company) {
              if (
                moment(company?.date_added).isAfter(moment('2025-01-22')) &&
                (is97Plan(company?.stripe_active_plan) || onboardingInfo?.enableLaunchpadV2 === true)
              ) {
                return resolve(true)
              }
            }

            let stepInfo: IStepInfo[] | null = null
            if (locationId in context.state.stepInfo) {
              stepInfo = context.state.stepInfo[locationId]
            } else {
              const launchpadProgressDocs = await LaunchpadProgress.getByRefId(locationId)
              stepInfo = launchpadProgressDocs.stepInfo
              context.commit('setStepInfo', { stepInfo: launchpadProgressDocs.stepInfo, locationId })
            }
            if (!stepInfo) {
              resolve(false)
            }
            if (stepInfo) {
              const allActionsCompleted = stepInfo.every(step => step.status === 'completed')
              if (allActionsCompleted) {
                return resolve(false)
              }
            }
            return resolve(true)
          } catch (e) {
            console.log('redirect to launchpad error', e)
            return resolve(false)
          }
        })
      },
    },
    isAgencyLaunchpadAssigned: {
      handler: (context: ActionContext<LaunchpadState, RootState>): Promise<any> => {
        return new Promise(async (resolve, reject) => {
          if (context.state.isAgencyLaunchpadAssigned !== null) {
            return resolve(context.state.isAgencyLaunchpadAssigned)
          } else {
            const companyId = context.rootState?.company?.company?.id
            try {
              if (!companyId) {
                return resolve(null)
              }
              const segmentAssignmentResp = await LaunchpadService.getSegmentAssigned({
                companyId,
              })
              context.commit('setIsAgencyLaunchpadAssigned', {
                isAgencyLaunchpadAssigned: segmentAssignmentResp?.segmentAssigned,
              })
              return resolve(segmentAssignmentResp?.segmentAssigned)
            } catch (err) {
              reject(err)
            }
          }
        })
      },
    },
    redirectToAgencyLaunchpad: {
      handler: (context: ActionContext<LaunchpadState, RootState>): Promise<any> => {
        return new Promise(async (resolve, reject) => {
          // true - redirect to agency launchpad
          // false - redirect to dashboard

          const userScopes = context.rootState?.user?.user?.scopes;
          if(!userScopes?.includes('agency/launchpad.write')) {
            return resolve(false)
          }

          try {
            const isAgencyLaunchpadAssigned = await context.dispatch('isAgencyLaunchpadAssigned')
            if (isAgencyLaunchpadAssigned) {
              return resolve(true)
            } else {
              return resolve(false)
            }
          } catch (e) {
            return resolve(false)
          }
        })
      },
    },
    setPiPVisibility: {
      handler: (context: ActionContext<LaunchpadState, RootState>, visibility: boolean): Promise<any> => {
        return new Promise(async (resolve, reject) => {
          try {
            context.commit('setPiPVisibility', { visibility })
            return resolve(visibility)
          } catch (e) {
            return resolve({})
          }
        })
      },
    },
    setVideoCompletionRate: {
      handler: (context: ActionContext<LaunchpadState, RootState>, videoCompletionRate: number): Promise<any> => {
        return new Promise(async (resolve, reject) => {
          try {
            context.commit('setVideoCompletionRate', { videoCompletionRate })
            return resolve(videoCompletionRate)
          } catch (e) {
            return resolve({})
          }
        })
      },
    },
    setSelectedProductId: {
      handler: (context: ActionContext<LaunchpadState, RootState>, selectedProductId: string): Promise<any> => {
        return new Promise(async (resolve, reject) => {
          try {
            context.commit('setSelectedProductId', { selectedProductId })
            return resolve(selectedProductId)
          } catch (e) {
            return resolve({})
          }
        })
      },
    },
    setSegmentId: {
      handler: (context: ActionContext<LaunchpadState, RootState>, segmentId: string): Promise<any> => {
        return new Promise(async (resolve, reject) => {
          try {
            context.commit('setSegmentId', { segmentId })
            return resolve(segmentId)
          } catch (e) {
            return resolve({})
          }
        })
      },
    },
    setSelectedProducts: {
      handler: (context: ActionContext<LaunchpadState, RootState>, selectedProducts: string[]): Promise<any> => {
        return new Promise(async (resolve, reject) => {
          try {
            context.commit('setSelectedProducts', { selectedProducts })
            return resolve(selectedProducts)
          } catch (e) {
            return resolve({})
          }
        })
      },
    },
    setNudgeCategory: {
      handler: (context: ActionContext<LaunchpadState, RootState>, nudgeCategoryDetails: object): Promise<any> => {
        return new Promise(async (resolve, reject) => {
          try {
            context.commit('setNudgeCategoryDetails', { nudgeCategory: nudgeCategoryDetails })
            return resolve(nudgeCategoryDetails)
          } catch (e) {
            return resolve({})
          }
        })
      },
    },
    setNudgeRules: {
      handler: (context: ActionContext<LaunchpadState, RootState>, nudgeRules: object[]): Promise<any> => {
        return new Promise(async (resolve, reject) => {
          try {
            context.commit('setNudgeRuleDetails', {
              nudgeRules: Array.isArray(context.state.nudgeRules)
                ? [...context.state.nudgeRules, ...nudgeRules]
                : [...nudgeRules],
            })
            return resolve(nudgeRules)
          } catch (e) {
            return resolve({})
          }
        })
      },
    },
    nudgeRuleTriggered: {
      handler: (context: ActionContext<LaunchpadState, RootState>, ruleNameTriggered: string): Promise<any> => {
        return new Promise(async (resolve, reject) => {
          try {
            context.commit('setNudgeRuleTriggered', { ruleNameTriggered })
            return resolve(ruleNameTriggered)
          } catch (e) {
            return resolve({})
          }
        })
      },
    },
    // stores the data for inactive-value-prop nudge
    setCurrentNudgeProduct: {
      handler: (context: ActionContext<LaunchpadState, RootState>, currentNudgeProduct: object): Promise<any> => {
        return new Promise(async (resolve, reject) => {
          try {
            context.commit('setCurrentNudgeProduct', { currentNudgeProduct })
            return resolve(currentNudgeProduct)
          } catch (e) {
            return resolve({})
          }
        })
      },
    },
    showLaunchpadLabs: {
      handler: (
        context: ActionContext<LaunchpadState, RootState>,
        params: {
          type: string
          locationId?: string
        }
      ): Promise<any> => {
        return new Promise(async (resolve, reject) => {
          try {
            const company = context.rootState?.company?.company
            const onboardingInfo = await context.dispatch(
              'onboarding/getOnboardingInfo',
              { companyId: company?.id },
              { root: true }
            )

            // since launchpad v2 is already enabled don't show in labs
            if (onboardingInfo?.enableLaunchpadV2) return resolve(false)

            // older agencies don't have defaultLocationId hence wont have default segment assigned
            if (params.type === 'location' && !params.locationId && !onboardingInfo?.defaultLocationId)
              return resolve(true)

            let response
            if (params.type === 'location') {
              response = await LaunchpadService.getSegmentAssigned({
                companyId: company?.id,
                locationId: params.locationId || onboardingInfo?.defaultLocationId,
              })
            } else if (params.type === 'company') {
              response = await LaunchpadService.getSegmentAssigned({ companyId: company?.id })
            }
            // here we check for default segment assigned if yes, don't show in labs
            return resolve(!response?.segmentAssigned)
          } catch (e) {
            console.error('showLaunchpadLabs error', e)
            return resolve(false)
          }
        })
      },
    },
  },
}
